import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import map from 'lodash/map';
import moment from 'moment';

import {getSavedSearchDates} from '@components/Common/SavedSearch/SavedSearchesActionButton/utils';
import {
  // DEPRECATED (HZFE_139) - remove the following when feature toggle is removed
  TRAVEL_SEARCH_AIRLINE_ID,
  TRAVEL_SEARCH_AIRLINE_IDS,
  TRAVEL_SEARCH_AIRPORT_ID,
  TRAVEL_SEARCH_DATE_RANGE,
  TRAVEL_SEARCH_DATE_RANGE_OPTIONS,
  TRAVEL_SEARCH_DESTINATION_TYPE,
  TRAVEL_SEARCH_DESTINATION_TYPE_VALUES,
  // DEPRECATED (HZFE_139) - remove the following when feature toggle is removed
  TRAVEL_SEARCH_FLIGHT_NUMBER_LIKE,
  TRAVEL_SEARCH_FLIGHT_NUMBERS,
  TRAVEL_SEARCH_ITINERARY_DELETED_STATUSES,
  TRAVEL_SEARCH_LOCATION_RATINGS,
  TRAVEL_SEARCH_LOCATION_RATINGS_ENABLED,
  TRAVEl_SEARCH_LOCATION_TYPES,
  TRAVEL_SEARCH_ORGS,
  TRAVEL_SEARCH_PERSON_CUSTOM_FIELDS,
  TRAVEL_SEARCH_PERSON_TYPES,
  TRAVEL_SEARCH_SERVICE_KEYS,
  TRAVEL_SEARCH_SHOW_DELETED_EXPATRIATIONS,
  TRAVEL_SEARCH_SHOW_DELETED_ITINERARIES,
  TRAVEL_SEARCH_TRAVEL_MNGMT_COMPANY,
  TRAVEL_SEARCH_TRIP_CUSTOM_FIELDS,
} from '@components/Persons/constants';

import {FEATURES} from '@constants/features';
import {DATE_RANGE_TYPE} from '@constants/person-groups';

import airlineApi from '@services/api/airlines';
import geoLocationsApi from '@services/api/geo-locations';

import {featureToggle} from '@utils/featureToggle/featureToggle';

export function formatDate(values) {
  const dateRange = values?.[TRAVEL_SEARCH_DATE_RANGE];

  if (!dateRange?.dateInput && !dateRange?.dateRangeInput) {
    return {
      dateFilterType: undefined,
      dateStart: undefined,
      dateEnd: undefined,
      [TRAVEL_SEARCH_DATE_RANGE]: undefined,
    };
  }

  let dateStart, dateEnd;

  if (dateRange?.noEndDate) {
    dateStart = dateRange.dateInput;
    dateEnd = moment.utc().add(100, 'years').toISOString();
  } else {
    dateStart = dateRange?.dateRangeInput[0].utcOffset(0, true).utc().toISOString();
    dateEnd = dateRange?.dateRangeInput[1].utcOffset(0, true).utc().toISOString();
  }

  // fallback to TRAVEL if undefined + date is defined
  const dateFilterType =
    dateRange?.[TRAVEL_SEARCH_DATE_RANGE_OPTIONS] || TRAVEL_SEARCH_SERVICE_KEYS.DATE_FILTER_TYPE.TRAVEL;
  return {
    dateFilterType,
    dateStart,
    dateEnd,
    [TRAVEL_SEARCH_DATE_RANGE]: undefined, // override property when spreading in formatForTravelSearch return
  };
}

export function formatPersonDetails(values) {
  const {include: PERSON_TYPE_INCLUSION_KEY, types} = values[TRAVEL_SEARCH_PERSON_TYPES] || {};

  const result = {[PERSON_TYPE_INCLUSION_KEY]: types};

  if (PERSON_TYPE_INCLUSION_KEY !== TRAVEL_SEARCH_PERSON_TYPES) {
    result[TRAVEL_SEARCH_PERSON_TYPES] = undefined; // remove personTypes from values if passing exclude
  }

  return result;
}

export function formatTravelTypesFromSavedSearch(filter) {
  const {personLocationTypes} = filter;
  return personLocationTypes
    ? {
        [TRAVEl_SEARCH_LOCATION_TYPES]: personLocationTypes,
      }
    : undefined;
}
export function formatLocationRatings(values) {
  return {
    [TRAVEL_SEARCH_LOCATION_RATINGS_ENABLED]: undefined,
    [TRAVEL_SEARCH_LOCATION_RATINGS]:
      values[TRAVEL_SEARCH_LOCATION_RATINGS_ENABLED] && values[TRAVEL_SEARCH_LOCATION_RATINGS]
        ? {
            start: values[TRAVEL_SEARCH_LOCATION_RATINGS][0],
            end: values[TRAVEL_SEARCH_LOCATION_RATINGS][1],
          }
        : undefined,
  };
}

export function formatLocationRatingsFromSavedSearch(filter) {
  return filter[TRAVEL_SEARCH_LOCATION_RATINGS]
    ? {
        [TRAVEL_SEARCH_LOCATION_RATINGS_ENABLED]: true,
        [TRAVEL_SEARCH_LOCATION_RATINGS]: [
          filter[TRAVEL_SEARCH_LOCATION_RATINGS].start,
          filter[TRAVEL_SEARCH_LOCATION_RATINGS].end,
        ],
      }
    : undefined;
}

export function formatDestinationType(values) {
  const destinationType = values[TRAVEL_SEARCH_DESTINATION_TYPE];
  if (
    destinationType?.includes(TRAVEL_SEARCH_DESTINATION_TYPE_VALUES.DOMESTIC) &&
    destinationType?.includes(TRAVEL_SEARCH_DESTINATION_TYPE_VALUES.INTERNATIONAL)
  ) {
    return {[TRAVEL_SEARCH_DESTINATION_TYPE]: TRAVEL_SEARCH_DESTINATION_TYPE_VALUES.ALL};
  }
  return {[TRAVEL_SEARCH_DESTINATION_TYPE]: destinationType?.[0]};
}

export function formatTravelManagementCompany(values) {
  return !isEmpty(values[TRAVEL_SEARCH_TRAVEL_MNGMT_COMPANY])
    ? {[TRAVEL_SEARCH_TRAVEL_MNGMT_COMPANY]: values[TRAVEL_SEARCH_TRAVEL_MNGMT_COMPANY]}
    : undefined;
}

function formatValuesLikeEqual(value) {
  const {valuesLike, valuesEqual} = value || {};
  const formatted = {
    ...(valuesLike?.length ? {valuesLike} : {}),
    ...(valuesEqual?.length ? {valuesEqual} : {}),
  };

  return Object.keys(formatted).length ? formatted : undefined;
}

function formatCustomFields(values, fieldName) {
  const fieldEntries = values?.[fieldName];

  const formatted = isEmpty(fieldEntries)
    ? undefined
    : Object.entries(fieldEntries).reduce((acc, [customFieldId, value]) => {
        const formattedValue = formatValuesLikeEqual(value);

        if (formattedValue) {
          acc.push({id: customFieldId, ...formattedValue});
        }

        return acc;
      }, []);

  return formatted?.length ? formatted : undefined;
}

export function formatForTravelSearch(values, sessionOrgId) {
  const formattedDate = formatDate(values);
  const formattedPersonDetails = formatPersonDetails(values);
  const formattedLocationRatings = formatLocationRatings(values);
  const formattedDestinationType = formatDestinationType(values);
  const formattedTravelManagementCompany = formatTravelManagementCompany(values);
  const formattedPersonCustomFields = formatCustomFields(values, TRAVEL_SEARCH_PERSON_CUSTOM_FIELDS);
  const formattedTripCustomFields = formatCustomFields(values, TRAVEL_SEARCH_TRIP_CUSTOM_FIELDS);

  return {
    ...values,
    ...formattedDate,
    ...formattedPersonDetails,
    ...formattedLocationRatings,
    ...formattedDestinationType,
    ...formattedTravelManagementCompany,
    [TRAVEL_SEARCH_ORGS]: isEmpty(values[TRAVEL_SEARCH_ORGS])
      ? sessionOrgId && [sessionOrgId]
      : values[TRAVEL_SEARCH_ORGS].map((org) => org.value),
    // DEPRECATED (HZFE_139) - remove ternary and keep only the true branch assignments when feature toggle is removed
    ...(featureToggle(FEATURES.HZFE_139)
      ? {
          // DEPRECATED (HZFE_139) - remove the following assignment when feature toggle is removed
          [TRAVEL_SEARCH_AIRLINE_ID]: undefined,
          [TRAVEL_SEARCH_AIRLINE_IDS]: values[TRAVEL_SEARCH_AIRLINE_IDS]?.length
            ? map(values[TRAVEL_SEARCH_AIRLINE_IDS], 'value')
            : undefined,
          // DEPRECATED (HZFE_139) - remove the following assignment when feature toggle is removed
          [TRAVEL_SEARCH_FLIGHT_NUMBER_LIKE]: undefined,
          [TRAVEL_SEARCH_FLIGHT_NUMBERS]: formatValuesLikeEqual(values[TRAVEL_SEARCH_FLIGHT_NUMBERS]),
        }
      : {
          // DEPRECATED (HZFE_139) - remove the following assignment when feature toggle is removed
          [TRAVEL_SEARCH_AIRLINE_ID]: values[TRAVEL_SEARCH_AIRLINE_ID]?.id,
          // DEPRECATED (HZFE_139) - remove the following assignment when feature toggle is removed
          [TRAVEL_SEARCH_FLIGHT_NUMBER_LIKE]: values[TRAVEL_SEARCH_FLIGHT_NUMBER_LIKE],
        }),
    [TRAVEL_SEARCH_AIRPORT_ID]: values[TRAVEL_SEARCH_AIRPORT_ID]?.id,
    [TRAVEL_SEARCH_SHOW_DELETED_EXPATRIATIONS]: values[TRAVEL_SEARCH_SHOW_DELETED_EXPATRIATIONS]
      ? Object.values(TRAVEL_SEARCH_ITINERARY_DELETED_STATUSES)
      : [TRAVEL_SEARCH_ITINERARY_DELETED_STATUSES.ACTIVE],
    [TRAVEL_SEARCH_SHOW_DELETED_ITINERARIES]: values[TRAVEL_SEARCH_SHOW_DELETED_ITINERARIES]
      ? Object.values(TRAVEL_SEARCH_ITINERARY_DELETED_STATUSES)
      : [TRAVEL_SEARCH_ITINERARY_DELETED_STATUSES.ACTIVE],
    [TRAVEL_SEARCH_PERSON_CUSTOM_FIELDS]: formattedPersonCustomFields,
    [TRAVEL_SEARCH_TRIP_CUSTOM_FIELDS]: formattedTripCustomFields,
  };
}

export const formatDestinationTypeForFilter = (savedSearch) => {
  const destinationType = savedSearch?.itineraryInternational;
  let result = undefined;

  if (destinationType && isString(destinationType)) {
    if (destinationType === 'ALL') {
      result = [TRAVEL_SEARCH_DESTINATION_TYPE_VALUES.DOMESTIC, TRAVEL_SEARCH_DESTINATION_TYPE_VALUES.INTERNATIONAL];
    } else {
      result = [destinationType];
    }
  } else {
    return result;
  }

  return result;
};

export const getAirlineInfo = async (filter) => {
  const airlineInfo = await airlineApi
    .airlineSearchList({
      params: {
        airlineId: filter.airlineId,
      },
    })
    .then((response) => {
      const {iataCode, name, id} = response?.data?.content?.[0] || {};
      const labelValue = `(${iataCode}) ${name}`;

      return {
        code: iataCode,
        id: id,
        label: labelValue,
        value: labelValue,
        key: id,
      };
    });
  return airlineInfo;
};

/**
 * Hydrate an array of airline UUIDs into [{value, label}] objects for display.
 * Each UUID is looked up via the airline search API in parallel.
 *
 * @param {string[]} airlineIds - Array of airline UUID strings
 * @returns {Promise<Array<{value: string, label: string}>>}
 */
export const getAirlineInfoList = async (airlineIds) => {
  if (!airlineIds?.length) return [];

  const results = await Promise.all(airlineIds.map((airlineId) => getAirlineInfo({airlineId})));

  return results.filter(Boolean).map(({id, label}) => ({value: id, label}));
};

export const getAirportInfo = async (filter) => {
  const airportInfo = await geoLocationsApi
    .getAirportDetails({
      pathParams: {
        id: filter.airportId,
      },
    })
    .then((response) => {
      const {iata, name, id} = response?.data || {};
      const labelValue = `(${iata}) ${name}`;
      return {
        iata: iata,
        id: id.toString(),
        label: labelValue,
        value: labelValue,
        key: iata,
      };
    });
  return airportInfo;
};

export const formatOrganizations = (savedFilter, organizationDict) => {
  if (!savedFilter?.organizationIds) {
    return undefined;
  } else {
    return savedFilter?.organizationIds
      ?.map((id) => {
        const organization = organizationDict[id];
        return organization ? {label: organization.label, value: organization.value} : null;
      })
      .filter((item) => item !== null);
  }
};

export const formatTripCustomFields = (savedFilter) => {
  return savedFilter?.itineraryCustomFields
    ? (savedFilter.itineraryCustomFields?.reduce((acc, item) => {
        // Formatting this way to easily remove the feature toggle

        if (item.id) {
          const valuesLike = Array.isArray(item.valuesLike)
            ? item.valuesLike
            : item.valuesLike
              ? [item.valuesLike]
              : [];
          const valuesEqual = Array.isArray(item.valuesEqual)
            ? item.valuesEqual
            : item.valuesEqual
              ? [item.valuesEqual]
              : [];

          // Raw under base id; visible merged under __uiInput
          acc[item.id] = {valuesLike, valuesEqual};
          const merged = [...valuesEqual, ...valuesLike];
          acc[`${item.id}__uiInput`] = merged.length ? merged : undefined;
        }

        return acc;
      }, {}) ?? undefined)
    : undefined;
};

export const formatShowDeletedTripsOrExpatriations = (savedFilter, fieldName) => {
  let result = false;
  const deletedStatuses = savedFilter?.[fieldName];

  if (isArray(deletedStatuses) && deletedStatuses.length === 2) {
    result = true;
  }

  return result;
};

export const formatPersonTypes = (values) => {
  if (values?.excludePersonTypes) {
    return {include: 'excludePersonTypes', types: values.excludePersonTypes};
  } else if (values?.personTypes) {
    return {include: 'personTypes', types: values.personTypes};
  }
};

export const formatPersonCustomFields = (savedFilter) => {
  if (!savedFilter?.customFields) {
    return undefined;
  }

  return savedFilter?.customFields?.reduce((acc, item) => {
    // Formatting this way to easily remove the feature toggle

    if (item.id) {
      const valuesLike = Array.isArray(item.valuesLike) ? item.valuesLike : item.valuesLike ? [item.valuesLike] : [];
      const valuesEqual = Array.isArray(item.valuesEqual)
        ? item.valuesEqual
        : item.valuesEqual
          ? [item.valuesEqual]
          : [];

      // Raw under base id; visible merged under __uiInput
      acc[item.id] = {valuesLike, valuesEqual};
      const merged = [...valuesEqual, ...valuesLike];
      acc[`${item.id}__uiInput`] = merged.length ? merged : undefined;
    }

    return acc;
  }, {});
};

// service returns IDs as ints. convert to strings
export const formatBookingCountryIds = (savedFilter) => {
  return savedFilter?.bookingCountryIds ? savedFilter.bookingCountryIds.map((num) => String(num)) : undefined;
};

export function formatDateFromSavedSearch(savedSearch) {
  const {filter, dateRangeType, audit} = savedSearch;
  const {dateFilterType} = filter;

  if (!dateFilterType && !filter.dateStart) {
    return undefined;
  }

  const isRelative = dateRangeType === DATE_RANGE_TYPE.RELATIVE;

  let filterUpdated = filter;

  if (isRelative) {
    const {dateStart, dateEnd} = getSavedSearchDates(filter.dateStart, filter.dateEnd, audit.updatedAt, isRelative);
    filterUpdated = {...filter, dateStart, dateEnd};
  }

  const utcDateStart = moment.utc(filterUpdated.dateStart);
  const utcDateEnd = moment.utc(filterUpdated.dateEnd);

  const diff = moment.duration(utcDateEnd.diff(utcDateStart));

  // service doesn't store/return noEndDate property, so just check if the end date is 100 years
  const noEndDate = Math.floor(diff.asYears()) >= 90;
  const dateOpts = !noEndDate
    ? {
        dateRangeInput: [utcDateStart, utcDateEnd],
        dateInput: undefined,
        noEndDate: false,
      }
    : {
        dateInput: utcDateStart,
        dateRangeInput: undefined,
        noEndDate: true,
      };

  const formattedDate = {
    travelSearchDateRange: {
      dateRangeOptions: dateFilterType,
      ...dateOpts,
    },
  };

  return formattedDate;
}
